/**
 * Validation results for an entity, for the quality lens.
 *
 * Read from the latest report in the dq dataset (/sparql-dq), the one the data quality portal
 * shows. Without that endpoint, the dev server's /dev-api/validation-results serves a report file
 * from this machine (server/validation-report-dev.ts).
 */

import { useEffect, useState } from 'react';
import config from '@config/env';

const SH = 'http://www.w3.org/ns/shacl#';

export interface ValidationResult {
  focusNode: string;
  resultPath?: string;
  severity: string;
  sourceShape?: string;
  message?: string;
  value?: string;
  /** From the test fixture, not from a report. */
  test?: boolean;
}

export type ResultLevel = 'error' | 'warn' | 'info';

export function resultLevel(result: ValidationResult): ResultLevel {
  if (result.severity === `${SH}Violation`) return 'error';
  if (result.severity === `${SH}Warning`) return 'warn';
  return 'info';
}

/** The local name of a shape IRI: reports from before the shape refactor use data/ IRIs. */
export const shapeName = (iri: string | undefined): string =>
  iri ? (iri.split(/[#/]/).pop() ?? iri) : '';

export interface ValidationState {
  status: 'loading' | 'ready' | 'none';
  /** File name of the report the results come from. */
  source: string | null;
  results: ValidationResult[];
}

const LATEST_REPORT = `PREFIX bno: <https://data.banenor.no/ontology/>
SELECT ?graph ?timestamp WHERE {
  ?summary a bno:ValidationSummary ; bno:timestamp ?timestamp ; bno:hasDetailedValidationReportGraph ?graph .
} ORDER BY DESC(?timestamp) LIMIT 1`;

const resultsQuery = (graph: string, focus: string[]) => `PREFIX sh: <${SH}>
SELECT ?focusNode ?path ?severity ?shape ?message ?value WHERE {
  VALUES ?focusNode { ${focus.map((iri) => `<${iri}>`).join(' ')} }
  GRAPH <${graph}> {
    ?result sh:focusNode ?focusNode ; sh:sourceShape ?shape .
    OPTIONAL { ?result sh:resultSeverity ?severity }
    OPTIONAL { ?result sh:resultPath ?path }
    OPTIONAL { ?result sh:resultMessage ?message }
    OPTIONAL { ?result sh:value ?value }
  }
}`;

type Binding = Record<string, { value: string } | undefined>;

async function select(query: string): Promise<Binding[]> {
  const response = await fetch('/sparql-dq', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/sparql-results+json' },
    body: new URLSearchParams({ query }),
  });
  if (!response.ok || !response.headers.get('Content-Type')?.includes('json')) throw new Error(`dq ${response.status}`);
  return (await response.json()).results.bindings;
}

let latest: Promise<{ graph: string; timestamp: string } | null> | null = null;

async function fromDq(focus: string[]): Promise<{ source: string; results: ValidationResult[] } | null> {
  latest ??= select(LATEST_REPORT).then(
    (rows) => (rows[0]?.graph ? { graph: rows[0].graph.value, timestamp: rows[0].timestamp?.value ?? '' } : null),
    (error: unknown) => {
      // A failed lookup is asked again on the next page, not kept for the session
      latest = null;
      throw error;
    }
  );
  const report = await latest;
  if (!report) return null;
  const results: ValidationResult[] = [];
  for (let start = 0; start < focus.length; start += 200) {
    const rows = await select(resultsQuery(report.graph, focus.slice(start, start + 200)));
    for (const row of rows) {
      results.push({
        focusNode: row.focusNode!.value,
        resultPath: row.path?.value,
        severity: row.severity?.value ?? `${SH}Violation`,
        sourceShape: row.shape?.value,
        message: row.message?.value.trim(),
        value: row.value?.value,
      });
    }
  }
  return { source: `dq ${report.timestamp.slice(0, 10)}`, results };
}

async function fromDevFile(focus: string[]): Promise<{ source: string | null; results: ValidationResult[] } | null> {
  const response = await fetch('/dev-api/validation-results', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ focus }),
  });
  const isJson = response.headers.get('Content-Type')?.includes('json') ?? false;
  return response.ok && isJson ? response.json() : null;
}

export function useValidationResults(focus: string[]): ValidationState {
  const [state, setState] = useState<ValidationState>({ status: 'loading', source: null, results: [] });
  const key = focus.join('\n');

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const focus = key.split('\n');
    setState({ status: 'loading', source: null, results: [] });
    fromDq(focus)
      .catch(() => null)
      // The report file on this machine: only the dev server serves it
      .then((dq) => dq ?? (config.isDevelopment ? fromDevFile(focus) : null))
      .then((body: { source: string | null; results: ValidationResult[] } | null) => {
        if (cancelled) return;
        setState(
          body && (body.source || body.results.length > 0)
            ? { status: 'ready', source: body.source, results: body.results }
            : { status: 'none', source: null, results: [] }
        );
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'none', source: null, results: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return state;
}
